import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react'
import { Box, InputBase, Popper, Typography } from '@mui/material'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import SearchIcon from '@mui/icons-material/Search'
import CheckIcon from '@mui/icons-material/Check'
import useTranslation from '@/hooks/useTranslation'
import type { GridEditorProps } from '@/components/grid/types'

export interface SearchableSelectParams {
  /** The options offered, already display-ordered. */
  values?: string[]
  /** Loads the options when the editor opens (the decoder list is per row). */
  fetchValues?: () => Promise<string[]>
  /** Shows a label for an option whose stored value is an id (station type). */
  optionLabel?: (option: string) => string
  /**
   * Draws an option as markup rather than one line of text — the decoder
   * picker stacks name, address and controller. `optionLabel` still drives
   * search, so filtering keeps working on the whole string.
   */
  renderOption?: (option: string) => ReactNode
  /** Widens the panel past the cell, for options that need more than a line. */
  minWidth?: number
  /**
   * Recognises the row's current option when the stored value is not the
   * option string itself. The decoder picker stores a bare "address-index"
   * but offers "name · address-index · controller", so without this the
   * current decoder is listed twice — once bare, once composite.
   */
  isInitial?: (option: string) => boolean
  /**
   * Offers "None" at the top of the list, for an optional field. Picking it
   * commits `'-'`, the grid's existing "no value" — the column's value setter
   * turns that back into null.
   */
  allowClear?: boolean
}

/** What "None" commits: the same blank the renderer already shows as `-`. */
const CLEAR_OPTION = '-'

/**
 * Searchable dropdown cell editor for the station grid: a search field over a
 * filtered, keyboard-navigable list.
 *
 * The panel is a Popper anchored to the cell rather than markup inside it — a
 * grid cell clips its overflow and sits inside a transformed (virtualised) row,
 * so a list rendered in place would be cut off at the cell's edge. Clicking
 * away or pressing Escape leaves the cell as it was; only picking an option
 * commits, because a half-typed search string is not a value.
 */
const SearchableSelectCellEditor: FC<GridEditorProps<any>> = ({
  value,
  params,
  onCommit,
  onCancel,
}) => {
  const { t } = useTranslation()
  const {
    values,
    fetchValues,
    optionLabel,
    renderOption,
    minWidth,
    isInitial,
    allowClear,
  } = (params ?? {}) as SearchableSelectParams

  // The cell value can be a plain string or an object (e.g. sub-area {name,id}).
  const normalize = (raw: unknown): string | null => {
    if (raw == null || raw === '' || raw === '-') return null
    if (typeof raw === 'object') return (raw as any).name ?? null
    return String(raw)
  }

  const initial = normalize(value)

  // The Popper anchors to the cell itself, so the element has to be in state:
  // a ref would still be null on the render that decides whether to open.
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  // Stable, so React does not detach and re-attach it (null, then the node) on
  // every render — which with a state setter is an endless render loop.
  const captureAnchor = useCallback((element: HTMLSpanElement | null) => {
    if (element) setAnchorEl(element.parentElement ?? element)
  }, [])
  const listRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)

  const [asyncValues, setAsyncValues] = useState<string[] | null>(null)
  const [loading, setLoading] = useState<boolean>(Boolean(fetchValues))

  useEffect(() => {
    if (!fetchValues) return
    let alive = true
    setLoading(true)
    Promise.resolve(fetchValues())
      .then((loaded) => {
        if (alive) setAsyncValues(Array.isArray(loaded) ? loaded.map(String) : [])
      })
      .catch(() => {
        if (alive) setAsyncValues([])
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const options = useMemo<string[]>(() => {
    const source = fetchValues ? (asyncValues ?? []) : (values ?? [])
    const list = source.map((option) => String(option))
    // Keep the current value selectable even if it is not in the option list.
    // Skip the fallback entry when the list already carries the current
    // option under a different string.
    const listed = isInitial
      ? list.some((option) => isInitial(option))
      : initial != null && list.includes(initial)
    const withCurrent = initial && !listed ? [initial, ...list] : list
    return allowClear ? [CLEAR_OPTION, ...withCurrent] : withCurrent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, asyncValues, initial, allowClear])

  const isClearOption = (option: string) =>
    Boolean(allowClear) && option === CLEAR_OPTION

  const getLabel = (option: string): string => {
    if (isClearOption(option)) return t('stationsSelectNone')
    return optionLabel ? String(optionLabel(option)) : option
  }

  /** The option the cell holds now — "None" when an optional cell is empty. */
  const isCurrent = (option: string) => {
    if (isClearOption(option)) return initial == null
    return isInitial ? isInitial(option) : option === initial
  }

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return options
    return options.filter((option) => getLabel(option).toLowerCase().includes(needle))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, query])

  // Keep the highlight in range and start it on the current value.
  useEffect(() => {
    const index = filtered.findIndex(isCurrent)
    setHighlight(index >= 0 ? index : 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, options.length])

  const scrollHighlightIntoView = (index: number) => {
    const element = listRef.current?.children?.[index] as HTMLElement | undefined
    element?.scrollIntoView({ block: 'nearest' })
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlight((current) => {
        const next = Math.min(current + 1, filtered.length - 1)
        scrollHighlightIntoView(next)
        return next
      })
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlight((current) => {
        const next = Math.max(current - 1, 0)
        scrollHighlightIntoView(next)
        return next
      })
    } else if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      if (filtered[highlight] != null) onCommit(filtered[highlight])
    } else if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      onCancel()
    }
  }

  const width = Math.max(anchorEl?.getBoundingClientRect().width ?? 220, 200)

  return (
    <Box
      component="span"
      ref={captureAnchor}
      data-no-cell-edit
      sx={{ display: 'block', width: '100%' }}
    >
      {/* Keeps the cell showing its value while the list is open. */}
      <Box
        component="span"
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'text.main',
        }}
      >
        {initial ? getLabel(initial) : ''}
      </Box>

      {anchorEl && (
        <ClickAwayListener onClickAway={onCancel}>
          <Popper
            open
            anchorEl={anchorEl}
            placement="bottom-start"
            style={{ zIndex: 1400 }}
            modifiers={[{ name: 'flip', enabled: true }]}
          >
            <Box
              sx={{
                width,
                // A multi-line option needs more room than the cell it drops
                // out of; the panel still caps at the viewport.
                ...(minWidth ? { minWidth, maxWidth: 'calc(100vw - 32px)' } : {}),
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'border.main',
                borderRadius: '10px',
                boxShadow: '0 10px 28px rgba(16, 24, 40, 0.18)',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  px: '10px',
                  height: 38,
                  borderBottom: '1px solid',
                  borderColor: 'border.devider',
                }}
              >
                <SearchIcon sx={{ fontSize: 16, color: 'grey.600' }} />
                <InputBase
                  autoFocus
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('formsPlaceholdersSearchPlaceholder')}
                  sx={{
                    flex: 1,
                    fontSize: 13,
                    color: 'text.main',
                    '& input::placeholder': { color: 'grey.600', opacity: 1 },
                  }}
                />
              </Box>

              <Box
                ref={listRef}
                sx={{ maxHeight: 240, overflowY: 'auto', py: '4px' }}
              >
                {loading && (
                  <Typography
                    sx={{ px: '12px', py: '10px', fontSize: 13, color: 'grey.600' }}
                  >
                    {t('commonStatusLoading')}
                  </Typography>
                )}
                {!loading && filtered.length === 0 && (
                  <Typography
                    sx={{ px: '12px', py: '10px', fontSize: 13, color: 'grey.600' }}
                  >
                    {t('stationsSelectNoMatches')}
                  </Typography>
                )}
                {filtered.map((option, index) => (
                  <Box
                    key={`${option}-${index}`}
                    onMouseEnter={() => setHighlight(index)}
                    onMouseDown={(event) => {
                      // mousedown (not click) so it fires before the input blur
                      event.preventDefault()
                      onCommit(option)
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      mx: '6px',
                      px: '10px',
                      ...(renderOption ? { py: '6px' } : { height: 34 }),
                      borderRadius: '7px',
                      cursor: 'pointer',
                      fontSize: 13,
                      color: 'text.main',
                      backgroundColor:
                        index === highlight ? 'rgba(0, 71, 68, 0.08)' : 'transparent',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        minWidth: 0,
                        ...(renderOption
                          ? {}
                          : {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }),
                      }}
                    >
                      {renderOption ? renderOption(option) : getLabel(option)}
                    </Box>
                    {isCurrent(option) && (
                      <CheckIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </Popper>
        </ClickAwayListener>
      )}
    </Box>
  )
}

export default SearchableSelectCellEditor
